import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Mail, Bell, Loader2, AlertTriangle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const TaskEmailPreferences = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    email_notifications: false,
    email_task_created: true,
    email_task_assigned: true,
    email_task_updated: true,
    email_task_completed: false,
    email_task_overdue: true,
    email_digest_enabled: false,
    email_digest_frequency: 'daily' as 'daily' | 'weekly',
  });

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setPreferences({
          email_notifications: data.email_notifications ?? false,
          email_task_created: data.email_task_created ?? true,
          email_task_assigned: data.email_task_assigned ?? true,
          email_task_updated: data.email_task_updated ?? true,
          email_task_completed: data.email_task_completed ?? false,
          email_task_overdue: data.email_task_overdue ?? true,
          email_digest_enabled: data.email_digest_enabled ?? false,
          email_digest_frequency: (data.email_digest_frequency as 'daily' | 'weekly') ?? 'daily',
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load email preferences',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          ...preferences,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Email preferences saved successfully',
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save email preferences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Mail className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold">Task Email Notifications</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Choose which task events trigger email notifications
          </p>
        </div>

        <Separator />

        {/* Master Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border-2 border-primary/20">
          <div className="space-y-0.5">
            <Label htmlFor="email_notifications" className="text-base font-semibold">
              Enable All Email Notifications
            </Label>
            <p className="text-sm text-muted-foreground">
              Master toggle to enable or disable all task email notifications
            </p>
          </div>
          <Switch
            id="email_notifications"
            checked={preferences.email_notifications}
            onCheckedChange={(checked) =>
              setPreferences({ ...preferences, email_notifications: checked })
            }
          />
        </div>

        {!preferences.email_notifications && (
          <Alert className="border-orange-500/50 bg-orange-500/10">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <AlertTitle className="text-orange-500">Email Notifications Disabled</AlertTitle>
            <AlertDescription className="text-orange-500/90">
              Enable the master toggle above to receive task email notifications.
              Individual notification settings will only work when the master toggle is ON.
            </AlertDescription>
          </Alert>
        )}

        <Separator />

        <div className={`space-y-4 ${!preferences.email_notifications ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email_task_created">New Task Created</Label>
              <p className="text-sm text-muted-foreground">
                Receive emails when a new task is created
              </p>
            </div>
            <Switch
              id="email_task_created"
              checked={preferences.email_task_created}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, email_task_created: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email_task_assigned">Task Assigned to Me</Label>
              <p className="text-sm text-muted-foreground">
                Receive emails when a task is assigned to you
              </p>
            </div>
            <Switch
              id="email_task_assigned"
              checked={preferences.email_task_assigned}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, email_task_assigned: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email_task_updated">Task Updated</Label>
              <p className="text-sm text-muted-foreground">
                Receive emails when a task is updated
              </p>
            </div>
            <Switch
              id="email_task_updated"
              checked={preferences.email_task_updated}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, email_task_updated: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email_task_completed">Task Completed</Label>
              <p className="text-sm text-muted-foreground">
                Receive emails when a task is marked as completed
              </p>
            </div>
            <Switch
              id="email_task_completed"
              checked={preferences.email_task_completed}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, email_task_completed: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email_task_overdue">Overdue Tasks</Label>
              <p className="text-sm text-muted-foreground">
                Receive emails about overdue tasks
              </p>
            </div>
            <Switch
              id="email_task_overdue"
              checked={preferences.email_task_overdue}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, email_task_overdue: checked })
              }
            />
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Bell className="h-4 w-4 text-primary" />
              <h4 className="text-sm font-semibold">Email Digest</h4>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Receive a summary of all your tasks in a single email
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="email_digest_enabled">Enable Email Digest</Label>
            <Switch
              id="email_digest_enabled"
              checked={preferences.email_digest_enabled}
              onCheckedChange={(checked) =>
                setPreferences({ ...preferences, email_digest_enabled: checked })
              }
            />
          </div>

          {preferences.email_digest_enabled && (
            <div className="space-y-2">
              <Label htmlFor="email_digest_frequency">Digest Frequency</Label>
              <Select
                value={preferences.email_digest_frequency}
                onValueChange={(value) =>
                  setPreferences({ ...preferences, email_digest_frequency: value as 'daily' | 'weekly' })
                }
              >
                <SelectTrigger id="email_digest_frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Preferences
          </Button>
        </div>
      </div>
    </Card>
  );
};
